import { useState, useEffect } from 'react'
import * as Tooltip from '@radix-ui/react-tooltip'
import { NavLink, Outlet, useNavigate, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Menu, ChevronLeft, ChevronRight, LayoutDashboard, Settings, LogOut, Building2, Users, FileText, X } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { PlatformRoleProvider } from '@/contexts/PlatformRoleContext'
import { usePlatformRoleContext } from '@/hooks/usePlatformRoleContext'
import { supabase } from '@/lib/supabaseClient'
import { cn } from '@/lib/utils'

const MAIN_NAV_ITEMS = [
  { to: '/portal', end: true, label: 'Dashboard', icon: LayoutDashboard },
  { to: '/portal/settings', end: false, label: 'Settings', icon: Settings },
] as const

const ADMIN_NAV_ITEMS = [
  { to: '/portal/briefs', end: false, label: 'Briefs', icon: FileText },
  { to: '/portal/businesses', end: false, label: 'Companies', icon: Building2 },
  { to: '/portal/users', end: false, label: 'Users', icon: Users },
] as const

const SIDEBAR_COLLAPSED_KEY = 'portal-sidebar-collapsed'

export default function ClientPortal() {
  const { user, loading, signOut } = useAuth()
  const navigate = useNavigate()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
    } catch {
      return false
    }
  })
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const check = () => {
      const nowMobile = window.innerWidth < 768
      setIsMobile(nowMobile)
      if (!nowMobile) setMobileMenuOpen(false)
    }
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])


  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed))
    } catch {
      // ignore
    }
  }, [sidebarCollapsed])


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-accent text-white">
        <Loader2 className="h-8 w-8 animate-spin text-white/70" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return (
    <PlatformRoleProvider>
      <ClientPortalLayout
        user={user}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        isMobile={isMobile}
        signOut={signOut}
        navigate={navigate}
      />
    </PlatformRoleProvider>
  )
}

function ClientPortalLayout({
  user,
  sidebarCollapsed,
  setSidebarCollapsed,
  mobileMenuOpen,
  setMobileMenuOpen,
  isMobile,
  signOut,
  navigate,
}: {
  user: { id: string; email: string }
  sidebarCollapsed: boolean
  setSidebarCollapsed: (v: boolean) => void
  mobileMenuOpen: boolean
  setMobileMenuOpen: (v: boolean) => void
  isMobile: boolean
  signOut: () => Promise<unknown>
  navigate: (to: string) => void
}) {
  const { canSeeClients } = usePlatformRoleContext()

  const { data: managedCount } = useQuery({
    queryKey: ['managed-business-count', user.id],
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('business_memberships')
        .select('business_id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('status', 'active')
        .in('role', ['business_owner', 'business_manager'])
      if (error) throw error
      return count ?? 0
    },
    enabled: !!user?.id,
  })

  const showManage = (managedCount ?? 0) > 0
  const companiesLabel = (managedCount ?? 0) === 1 ? 'Company' : 'Companies'

  const NavItem = ({
    to,
    end,
    label,
    icon: Icon,
  }: {
    to: string
    end: boolean
    label: string
    icon: React.ComponentType<{ className?: string }>
  }) => (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-lg text-sm font-medium transition-colors',
          sidebarCollapsed ? 'justify-center px-3 py-3' : 'gap-3 px-4 py-3',
          isActive ? 'bg-accent text-white' : 'text-primary/80 hover:text-primary hover:bg-black/5'
        )
      }
    >
      {sidebarCollapsed ? (
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <span className="flex justify-center">
                <Icon className="h-5 w-5 shrink-0" />
              </span>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                side="right"
                sideOffset={8}
                className="z-50 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground shadow-md"
              >
                {label}
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
      ) : (
        <>
          <Icon className="h-5 w-5 shrink-0" />
          {label}
        </>
      )}
    </NavLink>
  )

  const DesktopSidebar = () => (
    <Tooltip.Provider delayDuration={300}>
    <aside
      className={cn(
        'hidden md:flex shrink-0 flex-col bg-white text-primary transition-[width] duration-200 ease-in-out',
        sidebarCollapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex flex-col h-full">
        <div
          className={cn(
            'relative flex items-center',
            sidebarCollapsed ? 'justify-center p-3' : 'justify-center gap-2.5 p-5'
          )}
        >
          <NavLink to="/" className="flex items-center gap-2.5 h-12 min-w-0">
            <img
              src="/assets/gigafish-brandmark.svg"
              alt=""
              className={cn('w-auto shrink-0 img-accent', sidebarCollapsed ? 'h-10' : 'h-12')}
              aria-hidden
            />
            {!sidebarCollapsed && (
              <img src="/assets/gigafish-wordmark.svg" alt="Gigafish" className="h-5 w-auto img-accent" />
            )}
          </NavLink>
        </div>
        <div className="border-t border-neutral-200 mx-3" />
        <nav className="flex-1 p-3 overflow-y-auto overflow-x-hidden flex flex-col gap-6">
          <div className="space-y-1">
            {MAIN_NAV_ITEMS.map(({ to, end, label, icon }) => (
              <NavItem key={to} to={to} end={end} label={label} icon={icon} />
            ))}
          </div>
          {(canSeeClients || showManage) && (
            <>
              <div className="border-t border-neutral-200 mx-3" />
              <div className="space-y-1">
              {!sidebarCollapsed && (
                <div className="px-4 py-1.5 text-xs font-heading uppercase tracking-wider text-primary/50">
                  {canSeeClients ? 'Admin' : 'Manage'}
                </div>
              )}
              {canSeeClients ? (
                ADMIN_NAV_ITEMS.map(({ to, end, label, icon }) => (
                  <NavItem key={to} to={to} end={end} label={label} icon={icon} />
                ))
              ) : (
                <NavItem key="/portal/businesses" to="/portal/businesses" end={false} label={companiesLabel} icon={Building2} />
              )}
              </div>
            </>
          )}
        </nav>
        <div className="border-t border-neutral-200 mx-3" />
        <div className={cn(sidebarCollapsed ? 'p-2' : 'p-4')}>
          {!sidebarCollapsed && (
            <div className="px-4 py-2 text-xs text-primary/60 truncate">{user.email}</div>
          )}
          {sidebarCollapsed ? (
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    type="button"
                    onClick={async () => {
                      await signOut()
                      navigate('/login')
                    }}
                    className="flex w-full justify-center rounded-lg px-3 py-3 text-sm font-medium text-primary/80 hover:text-primary hover:bg-black/5 transition-colors"
                  >
                    <LogOut className="h-5 w-5 shrink-0" />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    side="right"
                    sideOffset={8}
                    className="z-50 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground shadow-md"
                  >
                    Log out
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
          ) : (
            <button
              type="button"
              onClick={async () => {
                await signOut()
                navigate('/login')
              }}
              className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-primary/80 hover:text-primary hover:bg-black/5 transition-colors"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              Log out
            </button>
          )}
        </div>
        <div className="border-t border-neutral-200 mx-3" />
        <div
          className={cn(
            'flex',
            sidebarCollapsed ? 'justify-center p-2' : 'justify-end p-2'
          )}
        >
          <button
            type="button"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 rounded-lg text-primary/60 hover:text-primary hover:bg-black/5 transition-colors"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </aside>
    </Tooltip.Provider>
  )

  const SIDEBAR_WIDTH = 256

  return (
    <div className="min-h-screen flex bg-accent font-sans overflow-x-hidden">
      <DesktopSidebar />

      {/* Mobile: nav slides in from left and pushes content */}
      <motion.aside
        initial={false}
        animate={{ x: mobileMenuOpen ? 0 : -SIDEBAR_WIDTH }}
        transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
        className="md:hidden fixed left-0 top-0 bottom-0 w-64 z-30 flex flex-col bg-white text-primary overflow-y-auto shadow-xl"
      >
        <div className="flex flex-col h-full min-h-full">
          <div className="flex items-center justify-between gap-2.5 p-5">
            <NavLink to="/" className="flex items-center gap-2.5 h-12 min-w-0 shrink" onClick={() => setMobileMenuOpen(false)}>
              <img src="/assets/gigafish-brandmark.svg" alt="" className="w-auto shrink-0 img-accent h-12" aria-hidden />
              <img src="/assets/gigafish-wordmark.svg" alt="Gigafish" className="h-5 w-auto img-accent" />
            </NavLink>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-lg text-primary/60 hover:text-primary hover:bg-black/5 -mr-2"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="border-t border-neutral-200 mx-3" />
          <nav className="flex-1 p-3 overflow-y-auto overflow-x-hidden flex flex-col gap-6">
            <div className="space-y-1">
              {MAIN_NAV_ITEMS.map(({ to, end, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                      isActive ? 'bg-accent text-white' : 'text-primary/80 hover:text-primary hover:bg-black/5'
                    )
                  }
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  {label}
                </NavLink>
              ))}
            </div>
            {(canSeeClients || showManage) && (
              <>
                <div className="border-t border-neutral-200 mx-3" />
                <div className="space-y-1">
                  <div className="px-4 py-1.5 text-xs font-heading uppercase tracking-wider text-primary/50">
                    {canSeeClients ? 'Admin' : 'Manage'}
                  </div>
                  {canSeeClients ? (
                    ADMIN_NAV_ITEMS.map(({ to, end, label, icon: Icon }) => (
                      <NavLink
                        key={to}
                        to={to}
                        end={end}
                        onClick={() => setMobileMenuOpen(false)}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                            isActive ? 'bg-accent text-white' : 'text-primary/80 hover:text-primary hover:bg-black/5'
                          )
                        }
                      >
                        <Icon className="h-5 w-5 shrink-0" />
                        {label}
                      </NavLink>
                    ))
                  ) : (
                    <NavLink
                      to="/portal/businesses"
                      end={false}
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                          isActive ? 'bg-accent text-white' : 'text-primary/80 hover:text-primary hover:bg-black/5'
                        )
                      }
                    >
                      <Building2 className="h-5 w-5 shrink-0" />
                      {companiesLabel}
                    </NavLink>
                  )}
                </div>
              </>
            )}
          </nav>
          <div className="border-t border-neutral-200 mx-3" />
          <div className="p-4">
            <div className="px-4 py-2 text-xs text-primary/60 truncate">{user.email}</div>
            <button
              type="button"
              onClick={async () => {
                setMobileMenuOpen(false)
                await signOut()
                navigate('/login')
              }}
              className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-primary/80 hover:text-primary hover:bg-black/5 transition-colors"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              Log out
            </button>
          </div>
        </div>
      </motion.aside>

      <motion.div
        initial={false}
        animate={{ x: isMobile && mobileMenuOpen ? SIDEBAR_WIDTH : 0 }}
        transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
        className="flex-1 flex flex-col min-w-0"
        onClick={() => mobileMenuOpen && setMobileMenuOpen(false)}
      >
        <header className="md:hidden relative flex items-center justify-center px-4 py-3 bg-accent border-b border-white/15">
          <div className="absolute left-4">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-white/80 hover:text-white"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
          <NavLink to="/" className="flex items-center gap-2.5 h-12">
            <img src="/assets/gigafish-brandmark.svg" alt="" className="h-12 w-auto brightness-0 invert" aria-hidden />
            <img src="/assets/gigafish-wordmark.svg" alt="Gigafish" className="h-6 w-auto brightness-0 invert" />
          </NavLink>
        </header>

        <main className="flex-1 relative overflow-auto">
          <div className="absolute inset-0 bg-[url('/assets/bg-noise.png')] opacity-10 mix-blend-overlay pointer-events-none" />
          <div className="relative z-10 p-6 md:p-8">
            <Outlet />
          </div>
        </main>
      </motion.div>
    </div>
  )
}

